import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Box, Typography, Paper, Button, TextField, InputAdornment, Divider,
  CircularProgress, Alert, Chip, Snackbar, Stack, Grid, Skeleton, IconButton,
  Checkbox, FormControlLabel, Link as MuiLink
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import API_BASE_URL from '../../Config/config';
import { useNextGenAuth } from '../context/NextGenAuthContext';

const CARD_BG = '#FFFFFF';
const BORDER = '1px solid #E5E7EB';

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const SummaryRow = ({ label, value, accent, strikethrough, large, helpText }) => (
  <Box sx={{ mb: large ? 1 : 0.8 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant={large ? 'body1' : 'body2'}
          sx={{ color: large ? '#1E293B' : '#64748B', fontWeight: large ? 700 : 500 }}
        >
          {label}
        </Typography>
        {helpText && (
          <InfoRoundedIcon sx={{ fontSize: 14, color: '#94A3B8', cursor: 'pointer' }} />
        )}
      </Box>
      <Typography
        variant={large ? 'h6' : 'body2'}
        sx={{
          fontWeight: large ? 800 : accent ? 700 : 600,
          color: accent ? '#10B981' : strikethrough ? '#94A3B8' : '#1E293B',
          textDecoration: strikethrough ? 'line-through' : 'none',
          letterSpacing: large ? '-0.02em' : 0,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

const NextGenPayment = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { nextgen_builder, updateBuilder } = useNextGenAuth();

  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [error, setError] = useState('');

  const [referralCode, setReferralCode] = useState('');
  const [referralApplied, setReferralApplied] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralError, setReferralError] = useState('');

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/plans`);
        const found = res.data.data.find((p) => p.id === planId);
        if (!found) {
          setError('Invalid plan selected. Please go back and select a plan.');
        } else {
          setPlan(found);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load plan details');
      } finally {
        setPlanLoading(false);
      }
    };
    fetchPlan();
  }, [planId]);

  const handleApplyReferral = async () => {
    if (!referralCode.trim()) {
      setReferralError('Please enter a referral code');
      return;
    }
    setReferralLoading(true);
    setReferralError('');
    setReferralApplied(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/nextgen-builder/referral/apply`,
        { referralCode: referralCode.trim().toUpperCase(), planId }
      );
      setReferralApplied(res.data.data);
      setSnack({ open: true, message: 'Referral code applied! Discount activated.', severity: 'success' });
    } catch (err) {
      setReferralError(err.response?.data?.message || 'Invalid or expired referral code');
    } finally {
      setReferralLoading(false);
    }
  };

  const removeReferral = () => {
    setReferralApplied(null);
    setReferralCode('');
    setReferralError('');
  };

  // Pricing calculations
  const getPricingData = () => {
    if (!plan) return null;

    const originalPrice = plan.price;
    const discount = referralApplied ? referralApplied.discount : 0;
    const subtotal = originalPrice - discount;
    const gstPercentage = plan.gstPercentage || 18;
    const gstAmount = Math.round((subtotal * (gstPercentage / 100)) * 100) / 100;
    const grandTotal = subtotal + gstAmount;

    return {
      originalPrice,
      discount,
      subtotal,
      gstPercentage,
      gstAmount,
      grandTotal
    };
  };

  const pricing = getPricingData();

  const handlePay = useCallback(async () => {
    if (!termsAccepted) {
      setShowTermsError(true);
      return;
    }

    setPaymentLoading(true);
    setError('');

    const loaded = await loadRazorpay();
    if (!loaded) {
      setError('Payment gateway failed to load. Please check your internet connection.');
      setPaymentLoading(false);
      return;
    }

    try {
      const orderRes = await axios.post(
        `${API_BASE_URL}/api/nextgen-builder/payment/create`,
        { planId, referralCode: referralApplied ? referralApplied.referralCode : undefined }
      );

      const { orderId, amount, razorpayKeyId, planName } = orderRes.data.data;
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};

      const options = {
        key: razorpayKeyId,
        amount: Math.round(amount * 100),
        currency: 'INR',
        name: 'The Skill Genie',
        description: `NextGen Builder — ${planName} Plan`,
        image: 'https://d1atm4dw3zgpab.cloudfront.net/assets/tsg-logo.png',
        order_id: orderId,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(
              `${API_BASE_URL}/api/nextgen-builder/payment/success`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }
            );
            if (verifyRes.data.success) {
              // Refresh the in-context subscription state immediately from
              // the verify response — NextGenLayout's sidebar and
              // NextGenPaidFeatureGuard both read this synchronously (no
              // extra API call), so AI Tutor / Elite Mastery / Mock
              // Interview unlock right away instead of only after the next
              // login.
              const { subscriptionPlan, validTill } = verifyRes.data.data || {};
              if (subscriptionPlan && validTill) {
                updateBuilder({ subscriptionPlan, subscriptionValidTill: validTill });
              }
              setSnack({ open: true, message: 'Payment successful! Subscription activated.', severity: 'success' });
              setTimeout(() => navigate('/nextgen-builder/dashboard'), 2000);
            } else {
              setError('Payment verification failed. Please contact support.');
            }
          } catch (verifyErr) {
            setError(verifyErr.response?.data?.message || 'Payment verification failed. Please contact support.');
          }
        },
        prefill: { name: user.fullName || '', email: user.email || '' },
        theme: { color: '#6C3BFF' },
        modal: {
          ondismiss: () => {
            setPaymentLoading(false);
            setSnack({ open: true, message: 'Payment cancelled', severity: 'warning' });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        setError(`Payment failed: ${response.error.description}`);
        setPaymentLoading(false);
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initiate payment');
      setPaymentLoading(false);
    }
  }, [planId, referralApplied, nextgen_builder?.id, navigate, termsAccepted]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto' }}>

        {/* Back navigation */}
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate('/nextgen-builder/dashboard')}
          sx={{
            mb: 4, color: '#64748B', fontWeight: 700,
            textTransform: 'none',
            '&:hover': { color: '#1E293B', bgcolor: 'transparent' },
          }}
        >
          Back to Dashboard
        </Button>

        {error && <Alert severity="error" sx={{ mb: 4, borderRadius: '12px' }}>{error}</Alert>}

        <Grid container spacing={4} alignItems="flex-start">
          {/* ── Left Column: Plan Details & Referral ── */}
          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              
              {/* Selected Plan Details */}
              <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', border: BORDER, bgcolor: '#FFFFFF' }}>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#1E293B', mb: 1 }}>
                    Review Your Plan
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
                    You are subscribing to the enterprise-grade NextGen Builder program.
                  </Typography>
                </Box>

                {planLoading ? (
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '16px' }} />
                ) : plan ? (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                      <Box>
                        <Chip 
                          label={plan.name} 
                          color="primary" 
                          sx={{ fontWeight: 800, mb: 1, height: 28, fontSize: '0.75rem', bgcolor: '#6C3BFF' }} 
                        />
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B' }}>
                          {plan.displayName || `${plan.name} Subscription`}
                        </Typography>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1E293B' }}>
                        ₹{plan.price.toLocaleString('en-IN')}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ color: '#475569', mb: 3, lineHeight: 1.6 }}>
                      {plan.description}
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 4 }}>
                      <Grid item xs={6}>
                        <Box sx={{ p: 2, borderRadius: '16px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                            Validity
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 800, color: '#1E293B' }}>
                            {plan.validityDays} Days
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ p: 2, borderRadius: '16px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                            Versions
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 800, color: '#1E293B' }}>
                            {plan.versionsAllowed} Version(s)
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', mb: 2 }}>
                      Key Features Included:
                    </Typography>
                    <Grid container spacing={1}>
                      {plan.features?.map((f, i) => (
                        <Grid item xs={12} sm={6} key={i}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#10B981' }} />
                            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>{f}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ) : null}
              </Paper>

              {/* Referral Section */}
              <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', border: BORDER, bgcolor: '#FFFFFF' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(108,59,255,0.1)', color: '#6C3BFF' }}>
                    <CardGiftcardRoundedIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1E293B' }}>
                      Referral Discount
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                      Apply a referral code to save on your purchase.
                    </Typography>
                  </Box>
                </Box>

                {referralApplied ? (
                  <Box
                    sx={{
                      p: 3, borderRadius: '20px',
                      background: 'rgba(16,185,129,0.05)',
                      border: '1px solid rgba(16,185,129,0.2)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleRoundedIcon sx={{ color: '#10B981', fontSize: 20 }} />
                        <Typography variant="body1" sx={{ fontWeight: 800, color: '#10B981' }}>
                          Code '{referralApplied.referralCode}' Applied!
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={removeReferral}
                        sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', bgcolor: 'rgba(239,68,68,0.1)' } }}
                      >
                        <CloseRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        Referred by: <strong>{referralApplied.referrerName}</strong>
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#10B981', fontWeight: 800 }}>
                        Savings: ₹{referralApplied.discount.toLocaleString('en-IN')}
                      </Typography>
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Enter referral code"
                      value={referralCode}
                      onChange={(e) => {
                        setReferralCode(e.target.value.toUpperCase());
                        setReferralError('');
                      }}
                      error={!!referralError}
                      helperText={referralError}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '14px',
                          bgcolor: '#F8FAFC',
                          '& fieldset': { borderColor: '#E2E8F0' },
                          '&:hover fieldset': { borderColor: '#CBD5E1' },
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleApplyReferral}
                      disabled={referralLoading || !referralCode.trim()}
                      sx={{
                        px: 4, borderRadius: '14px', fontWeight: 800,
                        bgcolor: '#1E293B', color: '#FFFFFF',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#0F172A' },
                        height: '56px'
                      }}
                    >
                      {referralLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Apply'}
                    </Button>
                  </Box>
                )}
              </Paper>
            </Stack>
          </Grid>

          {/* ── Right Column: Order Summary & Pay ── */}
          <Grid item xs={12} md={5}>
            <Stack spacing={3} sx={{ position: 'sticky', top: 32 }}>
              <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', border: BORDER, bgcolor: '#FFFFFF', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.04)' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B', mb: 3 }}>
                  Order Summary
                </Typography>

                {planLoading ? (
                  <Stack spacing={2}>
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Divider />
                    <Skeleton variant="text" width="100%" height={50} />
                  </Stack>
                ) : pricing ? (
                  <>
                    <SummaryRow label="Plan Price" value={`₹${pricing.originalPrice.toLocaleString('en-IN')}`} />
                    
                    {pricing.discount > 0 && (
                      <SummaryRow 
                        label="Referral Discount" 
                        value={`−₹${pricing.discount.toLocaleString('en-IN')}`} 
                        accent 
                      />
                    )}

                    <SummaryRow label="Subtotal" value={`₹${pricing.subtotal.toLocaleString('en-IN')}`} />
                    <SummaryRow label={`GST (${pricing.gstPercentage}%)`} value={`₹${pricing.gstAmount.toLocaleString('en-IN')}`} />
                    
                    <Divider sx={{ my: 2.5, borderStyle: 'dashed' }} />
                    
                    <SummaryRow 
                      label="Grand Total" 
                      value={`₹${pricing.grandTotal.toLocaleString('en-IN')}`} 
                      large 
                    />

                    <Box sx={{ mt: 4, mb: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            size="small"
                            checked={termsAccepted}
                            onChange={(e) => {
                              setTermsAccepted(e.target.checked);
                              if (e.target.checked) setShowTermsError(false);
                            }}
                            sx={{ color: showTermsError ? '#EF4444' : '#64748B', '&.Mui-checked': { color: '#6C3BFF' } }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500, lineHeight: 1.5 }}>
                            I have read, understood and agree to the{' '}
                            <MuiLink component={Link} to="/terms" target="_blank" sx={{ color: '#6C3BFF', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                              Terms & Conditions
                            </MuiLink>{' '}
                            and{' '}
                            <MuiLink component={Link} to="/privacy" target="_blank" sx={{ color: '#6C3BFF', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                              Privacy Policy
                            </MuiLink>
                          </Typography>
                        }
                        sx={{ alignItems: 'flex-start', mt: -1 }}
                      />
                      {showTermsError && (
                        <Typography variant="caption" sx={{ color: '#EF4444', fontWeight: 600, ml: 4, mt: 0.5, display: 'block' }}>
                          Please accept Terms & Conditions before proceeding.
                        </Typography>
                      )}
                    </Box>

                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={paymentLoading || planLoading}
                      onClick={handlePay}
                      sx={{
                        py: 2.25,
                        borderRadius: '16px',
                        fontWeight: 800,
                        fontSize: '1.1rem',
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #6C3BFF 0%, #4F46E5 100%)',
                        boxShadow: '0 10px 25px -5px rgba(108, 59, 255, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5A2EE5 0%, #4338CA 100%)',
                          boxShadow: '0 15px 30px -5px rgba(108, 59, 255, 0.5)',
                          transform: 'translateY(-2px)'
                        },
                        '&:disabled': { opacity: 0.7, color: 'rgba(255,255,255,0.8)' },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {paymentLoading ? (
                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                      ) : (
                        `Pay ₹${pricing.grandTotal.toLocaleString('en-IN')} Securely`
                      )}
                    </Button>

                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <SecurityRoundedIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
                          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600 }}>SSL Encrypted</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <VerifiedUserRoundedIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
                          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600 }}>Razorpay Secure</Typography>
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#94A3B8', textAlign: 'center', display: 'block', px: 2 }}>
                        This is a one-time secure payment. No hidden charges or automatic renewals.
                      </Typography>
                    </Box>
                  </>
                ) : null}
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snack.severity} sx={{ borderRadius: '12px', fontWeight: 600 }}>
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </motion.div>
  );
};

export default NextGenPayment;
